import { useEffect, useMemo, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Spinner } from "@/components/ui/spinner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import EmptyState from "@/components/ui/empty-state";
import {
  AlertCircleIcon,
  ArrowLeft,
  ArrowRight,
  ArrowRightLeftIcon,
  FileQuestionIcon,
} from "lucide-react";
import { SelectList, SelectListItem } from "@/components/ui/select-list";
import { ItemContent, ItemDescription, ItemTitle } from "@/components/ui/item";
import { TransferCreatorsDialogProps } from "../types";

export default function TransferCreatorsDialog({
  selectedCount,
  eligible,
  campaigns,
  onTransfer,
}: TransferCreatorsDialogProps) {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState<"pick" | "confirm">("pick");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) {
      setSelectedId(null);
      setSubmitting(false);
      setStep("pick");
    }
  }, [open]);

  const selectedCampaign = useMemo(
    () => campaigns.find((c) => c._id === selectedId) ?? null,
    [campaigns, selectedId]
  );

  if (selectedCount === 0 || !eligible) return null;

  const handleConfirm = async () => {
    if (!selectedId) return;
    setSubmitting(true);
    try {
      await onTransfer(selectedId);
      setOpen(false);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger asChild>
        <Button variant="ghost" size="xs">
          <ArrowRightLeftIcon
            className="opacity-60 size-4"
            aria-hidden="true"
          />
          Transfer
          <Badge variant="count" className="-me-1">
            {selectedCount}
          </Badge>
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent className="max-w-lg">
        {step === "pick" && (
          <>
            <AlertDialogHeader>
              <AlertDialogTitle>Transfer creators to another campaign</AlertDialogTitle>
              <AlertDialogDescription>
                Pick a same-platform campaign to move{" "}
                {selectedCount} selected creator{selectedCount === 1 ? "" : "s"} into.
              </AlertDialogDescription>
            </AlertDialogHeader>

            {campaigns.length === 0 ? (
              <div className="w-full flex items-center justify-center py-6">
                <EmptyState
                  icon={<FileQuestionIcon />}
                  text="No other campaigns on this platform can receive creators"
                />
              </div>
            ) : (
              <SelectList
                value={selectedId ?? ""}
                onValueChange={setSelectedId}
                className="max-h-100"
              >
                {campaigns.map((c) => (
                  <SelectListItem key={c._id} value={c._id}>
                    <ItemContent className="min-w-0 gap-0">
                      <ItemTitle className="truncate">
                        {c.campaign_name || "Untitled campaign"}
                      </ItemTitle>
                      {c.platform && (
                        <ItemDescription className="capitalize">
                          {c.platform}
                          {" · "}
                          {String(c.status).toUpperCase() === "OUTREACH" ? "Running" : "Search"}
                        </ItemDescription>
                      )}
                    </ItemContent>
                  </SelectListItem>
                ))}
              </SelectList>
            )}

            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <Button
                onClick={() => setStep("confirm")}
                disabled={!selectedId}
                variant="neutral"
              >
                Next
                <ArrowRight />
              </Button>
            </AlertDialogFooter>
          </>
        )}

        {step === "confirm" && (
          <>
            <AlertDialogHeader>
              <AlertDialogTitle>Transfer creators?</AlertDialogTitle>
              <AlertDialogDescription>
                {selectedCount} creator{selectedCount === 1 ? "" : "s"} will be moved to{" "}
                <span className="font-medium text-foreground">
                  {selectedCampaign?.campaign_name || "the selected campaign"}
                </span>
                .
              </AlertDialogDescription>
            </AlertDialogHeader>
            <Alert variant="warning">
              <AlertTitle>
                <AlertCircleIcon />
                These creators will leave this campaign.
              </AlertTitle>
              <AlertDescription>
                Matching field values are kept; fields not used by the target
                campaign are dropped. Creators already in the target are skipped.
                {String(selectedCampaign?.status).toUpperCase() === "OUTREACH"
                  ? " The target campaign is running, so transferred creators join its outreach queue and will be emailed automatically."
                  : ""}{" "}
                Creators who were already contacted or negotiating here start
                over with a fresh email thread in the target campaign — the
                existing conversation and any negotiation progress are not
                carried over.
              </AlertDescription>
            </Alert>
            <AlertDialogFooter>
              <Button
                variant="secondary"
                onClick={() => setStep("pick")}
                disabled={submitting}
              >
                <ArrowLeft />
                Back
              </Button>
              <AlertDialogAction
                onClick={(e) => {
                  e.preventDefault();
                  handleConfirm();
                }}
                variant="neutral"
                disabled={submitting}
              >
                {submitting && <Spinner />}
                {!submitting && <ArrowRightLeftIcon />}
                Transfer creators
              </AlertDialogAction>
            </AlertDialogFooter>
          </>
        )}
      </AlertDialogContent>
    </AlertDialog>
  );
}
